import { procoreClient } from "./client";

/** v1.0 of the download endpoint wraps the payload in `data`; later versions return it flat. */
type SignedDownloadResponse = {
    download_url?: string;
    data?: { download_url?: string };
};

/**
 * Matches a Document Management revision download endpoint, e.g.
 * `/rest/v1.0/companies/:id/projects/:id/collaborative_documents/document_revisions/:id/download`.
 *
 * Sheets federated from Document Management carry one of these as their `download_url`; sheets
 * from the Drawings tool carry a signed storage URL instead.
 */
export function isPdmDownloadUrl(url: string): boolean {
    const lower = url.toLowerCase();

    return (
        lower.includes("/collaborative_documents/") &&
        lower.includes("/document_revisions/") &&
        lower.endsWith("/download")
    );
}

/** Origin-stripped path with `no_redirect` set, for `procoreClient` to prepend its base URL to. */
function toNoRedirectPath(url: string): string {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set("no_redirect", "true");

    return `${parsed.pathname}${parsed.search}`;
}

/**
 * Resolve a sheet's `download_url` to a URL whose bytes can be fetched directly.
 *
 * Document Management downloads take two hops. The `/download` endpoint authenticates the caller
 * and answers 302 to signed storage, but the browser cannot follow that redirect on our behalf:
 * the request carries the Procore session (or a bearer token), and storage rejects both
 * credentialed and preflighted cross-origin requests. So we ask for the signed URL as JSON via
 * `no_redirect`, the same contract the Procore document viewer uses.
 *
 * The request goes through `procoreClient` so it inherits the app's auth and base URL —
 * same-origin in production, the Vite proxy locally. That matters because `download_url` is
 * absolute and points at the regional API host (e.g. https://api.procore.com), which is
 * cross-origin in production: fetched verbatim the session cookie is never sent and the endpoint
 * answers 401 (BM-12928).
 *
 * Signed storage URLs are returned unchanged — they authenticate through their own signature.
 */
export async function resolveDrawingFileUrl(url: string): Promise<string> {
    if (!isPdmDownloadUrl(url)) {
        return url;
    }

    const response = await procoreClient<SignedDownloadResponse>(toNoRedirectPath(url), { method: "GET" });
    const downloadUrl = response.data?.download_url ?? response.download_url;

    if (!downloadUrl) {
        throw new Error(`download_url missing from response for ${url}`);
    }

    return downloadUrl;
}
